/**
 * PDF Monitoring Service
 *
 * Frontend service for monitoring PDF generation queue and worker status.
 * Connects to Pages Function API which proxies to PDF worker via service binding.
 */

// Type Definitions
export interface PDFMetrics {
  summary: {
    pending?: number;
    queued?: number;
    processing?: number;
    completed?: number;
    failed?: number;
  };
  performance: {
    avgProcessingMinutes: number;
    avgRetryCount: number;
  };
  last24Hours: {
    completed: number;
    failed: number;
  };
  timestamp: string;
}

export interface PDFJob {
  tracking_id: string;
  status: 'pending' | 'queued' | 'processing' | 'completed' | 'failed';
  queued_at: string;
  completed_at?: string;
  retry_count: number;
  pdf_url?: string;
  markdown_r2_key?: string;
  metadata_r2_key?: string;
  error?: string;
  processing_time_minutes?: number;
}

export interface PDFError {
  error_message: string;
  count: number;
  last_occurrence: string;
  tracking_ids: string[];
}

export interface QueueStatus {
  queued: number;
  processing: number;
  pending: number;
  timestamp: string;
}

export interface MonitoringResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

// API Base URL - uses relative path for Pages Functions
const API_BASE_URL = '/api/pdf-monitoring';

export class PDFMonitoringService {
  /**
   * Fetch PDF generation metrics
   */
  static async getMetrics(): Promise<MonitoringResponse<PDFMetrics>> {
    try {
      const response = await fetch(`${API_BASE_URL}/metrics`);

      if (!response.ok) {
        const errorData = await response.json();
        return {
          success: false,
          error: errorData.error || `HTTP ${response.status}`
        };
      }

      const data = await response.json();
      return {
        success: true,
        data
      };
    } catch (error: any) {
      console.error('Failed to fetch PDF metrics:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Fetch recent PDF generation jobs
   */
  static async getRecentJobs(limit: number = 20): Promise<MonitoringResponse<{ jobs: PDFJob[]; count: number; timestamp: string }>> {
    try {
      const response = await fetch(`${API_BASE_URL}/recent-jobs?limit=${limit}`);

      if (!response.ok) {
        const errorData = await response.json();
        return {
          success: false,
          error: errorData.error || `HTTP ${response.status}`
        };
      }

      const data = await response.json();
      return {
        success: true,
        data
      };
    } catch (error: any) {
      console.error('Failed to fetch recent jobs:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Fetch error analysis
   */
  static async getErrors(): Promise<MonitoringResponse<{ errors: PDFError[]; total: number; timestamp: string }>> {
    try {
      const response = await fetch(`${API_BASE_URL}/errors`);

      if (!response.ok) {
        const errorData = await response.json();
        return {
          success: false,
          error: errorData.error || `HTTP ${response.status}`
        };
      }

      const data = await response.json();
      return {
        success: true,
        data
      };
    } catch (error: any) {
      console.error('Failed to fetch errors:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Fetch queue status
   */
  static async getQueueStatus(): Promise<MonitoringResponse<QueueStatus>> {
    try {
      const response = await fetch(`${API_BASE_URL}/queue-status`);

      if (!response.ok) {
        const errorData = await response.json();
        return {
          success: false,
          error: errorData.error || `HTTP ${response.status}`
        };
      }

      const data = await response.json();
      return {
        success: true,
        data
      };
    } catch (error: any) {
      console.error('Failed to fetch queue status:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Fetch all monitoring data in one call
   */
  static async getAllData() {
    const [metrics, recentJobs, errors, queueStatus] = await Promise.all([
      this.getMetrics(),
      this.getRecentJobs(20),
      this.getErrors(),
      this.getQueueStatus()
    ]);

    return {
      metrics: metrics.data || null,
      recentJobs: recentJobs.data?.jobs || [],
      errors: errors.data?.errors || [],
      queueStatus: queueStatus.data || null,
      hasError: !metrics.success || !recentJobs.success || !errors.success || !queueStatus.success
    };
  }
}
